import { UNIT_SYSTEMS, type UnitSystem } from '@/features/units';
import type { GlideSettings } from './types';

/**
 * Heights exactly as typed by the user. They remember the unit system they were typed in, so switching
 * units only changes how they are displayed, never the value used for computing.
 */
export interface MeasuredText {
  text: string;
  system: UnitSystem;
}

export interface SettingsInput {
  glideRatio: number;
  clearance: MeasuredText;
}

export type MeasuredSetting = 'clearance';

/** Range of the glide ratio slider. */
export const GLIDE_RATIO_RANGE = { min: 1.5, max: 10, step: 0.1 } as const;

/** Fixed grid: 50 m cells over at most 30 km around the origin. */
const CELL_SIZE_M = 50;
const MAX_RADIUS_M = 30000;

export const DEFAULT_SETTINGS_INPUT: SettingsInput = {
  glideRatio: 3.5,
  clearance: { text: '0', system: 'metric' },
};

/** Spinner steps of the height inputs, in the displayed unit. */
export const HEIGHT_INPUT_STEPS: Record<UnitSystem, { clearance: number }> = {
  metric: { clearance: 1 },
  imperial: { clearance: 5 },
};

/** A typed height as shown in `system`, rounded to whole units. */
export function displayText(input: SettingsInput, key: MeasuredSetting, system: UnitSystem): string {
  const { text, system: from } = input[key];
  if (from === system) return text;
  return String(
    Math.round((Number(text) * UNIT_SYSTEMS[from].height.metres) / UNIT_SYSTEMS[system].height.metres),
  );
}

/** Parses the typed settings into metres, replacing empty/invalid values with defaults. */
export function resolveSettings(input: SettingsInput): GlideSettings {
  const height = ({ text, system }: MeasuredText) =>
    Math.max(0, Number(text) || 0) * UNIT_SYSTEMS[system].height.metres;
  const { min, max } = GLIDE_RATIO_RANGE;
  return {
    glideRatio: Math.min(max, Math.max(min, input.glideRatio)),
    clearance: height(input.clearance),
    cellSize: CELL_SIZE_M,
    maxRadius: MAX_RADIUS_M,
  };
}
